import React from 'react';
import styled from 'styled-components';
import Icon from './Icon';
import Loading from '../../components/Loading/Loading';
import Tooltip from '../../components/Tooltip';
import Typography from './Typography';

export type ButtonSize =
  | 'X-Small - Circle'
  | 'X-Small'
  | 'Small'
  | 'Medium'
  | 'Large';
export type ButtonType =
  | 'Primary'
  | 'Secondary'
  | 'Selected'
  | 'Tertiary'
  | 'Ghost'
  | 'Ghost - No Padding'
  | 'Danger'
  | 'Danger Secondary';

interface ButtonProps {
  /**
   * How large should the button be?
   */
  size: ButtonSize;

  /**
   * What type of button this button?
   */
  type: ButtonType;

  /**
   * Button label (optional)
   */
  label?: string;

  /**
   * Muted text shown after the label, e.g. a record count (optional)
   */
  trailingLabel?: string;

  /**
   * Shows a spinner in place of the trailing label while it loads (optional)
   */
  isTrailingLabelLoading?: boolean;

  /**
   * Icon (optional)
   */
  icon?: Kingpin.Icon;

  /**
   * Override the icon color determined by the button's type
   */
  iconColor?: string;

  /**
   * Icon after (optional)
   */
  iconAfter?: Kingpin.Icon;

  /**
   * Is this button waiting on something? (optional)
   */
  isLoading?: boolean;

  /**
   * Is this button disabled? (optional)
   */
  isDisabled?: boolean;

  /**
   * Tooltip to show when button is disabled (optional)
   */
  isDisabledMessage?: string;

  /**
   * Click handler (options)
   */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;

  /**
   * Id for tests
   */
  testId?: string;

  hasNoPadding?: boolean;
}

const PRIMARY_BACKGROUND_COLOR = 'var(--color-bg-inverse)';
const SECONDARY_BACKGROUND_COLOR = 'var(--color-bg-primary)';
const SELECTED_BACKGROUND_COLOR = 'var(--color-bg-selected)';
const TERTIARY_BACKGROUND_COLOR = 'var(--color-bg-tertiary)';
const GHOST_BACKGROUND_COLOR = 'transparent';
export const GHOST_HOVER_BACKGROUND_COLOR = 'var(--color-bg-hover)';
const DANGER_BACKGROUND_COLOR = 'var(--color-status-danger-solid)';
const DANGER_HOVER_BACKGROUND_COLOR = 'var(--color-status-danger-solid-hover)';

const PRIMARY_TEXT_COLOR = 'var(--color-bg-primary)';
const SECONDARY_TEXT_COLOR = 'var(--color-text-secondary)';
const SELECTED_TEXT_COLOR = 'var(--color-info-v2)';
const TERTIARY_TEXT_COLOR = 'var(--color-text-secondary)';
const GHOST_TEXT_COLOR = 'var(--color-info-v2)';
const DANGER_TEXT_COLOR = 'var(--color-status-danger-solid-text)';
const DANGER_SECONDARY_TEXT_COLOR = 'var(--color-status-danger)';

const getTrailingTextColor = (type: ButtonType) =>
  type === 'Primary'
    ? 'var(--color-text-inverse-secondary)'
    : 'var(--color-text-tertiary)';

const getBackgroundColor = (type: ButtonType) => {
  switch (type) {
    case 'Primary':
      return PRIMARY_BACKGROUND_COLOR;
    case 'Secondary':
    case 'Danger Secondary':
      return SECONDARY_BACKGROUND_COLOR;
    case 'Selected':
      return SELECTED_BACKGROUND_COLOR;
    case 'Tertiary':
      return TERTIARY_BACKGROUND_COLOR;
    case 'Ghost':
      return GHOST_BACKGROUND_COLOR;
    case 'Danger':
      return DANGER_BACKGROUND_COLOR;
  }
};

const getBorder = (type: ButtonType) => {
  switch (type) {
    case 'Secondary':
    case 'Danger Secondary':
      return '1px solid var(--color-border-primary)';
    case 'Tertiary':
      return '1px solid var(--color-border-primary)';
    case 'Selected':
    case 'Primary':
    case 'Ghost':
    case 'Danger':
      return 'unset';
  }
};

const getPadding = ({
  size,
  hasLabel,
}: {
  size: ButtonSize;
  hasLabel: boolean;
}) => {
  if (size === 'X-Small' || size === 'X-Small - Circle') {
    return '0px';
  } else if (size === 'Small') {
    if (hasLabel) {
      return '4px 10px';
    }
    return '0px';
  } else if (size === 'Medium') {
    if (hasLabel) {
      return '6px';
    }
    return '0px';
  }

  return 'unset';
};

const getHeight = ({ type, size }: { type: ButtonType; size: ButtonSize }) => {
  if (type === 'Ghost') {
    if (size === 'X-Small' || size === 'X-Small - Circle') {
      return 14;
    }

    if (size === 'Small') {
      return 20;
    }
    return 24;
  }

  if (size === 'X-Small' || size === 'X-Small - Circle') {
    return 26;
  }

  if (size === 'Small') {
    return 32;
  }

  if (size === 'Large') {
    return 40;
  }

  return 36;
};

const ButtonDiv = styled.div<{
  type: ButtonType;
  size: ButtonSize;
  hasLabel: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  hasNoPadding?: boolean;
}>`
  background-color: ${(props) => getBackgroundColor(props.type)};
  border: ${(props) => getBorder(props.type)};
  padding: ${(props) =>
    props.hasNoPadding
      ? 0
      : getPadding({ size: props.size, hasLabel: props.hasLabel })};
  border-radius: ${(props) =>
    props.size === 'X-Small - Circle' ? '50%' : '10px'};
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${(props) => getHeight({ type: props.type, size: props.size })}px;
  width: ${(props) =>
    props.hasLabel
      ? 'unset'
      : getHeight({ type: props.type, size: props.size })}px;

  ${(props) =>
    props.type !== 'Ghost' &&
    `
    box-shadow:
      0px 3px 2px -1px rgba(0, 0, 0, 0.02),
      0px 1px 1px -1px rgba(0, 0, 0, 0.04);
  `}

  svg {
    margin-right: ${(props) => (props.hasLabel ? '4px' : '0px')};
    width: 16px;
    height: 16px;
  }

  cursor: ${(props) =>
    props.isLoading ? 'wait' : props.isDisabled ? 'not-allowed' : 'pointer'};

  opacity: ${(props) => (props.isDisabled ? 0.7 : 1)};

  transition: background-color 150ms ease;

  &:hover {
    ${(props) =>
      props.isLoading || props.isDisabled
        ? ''
        : `
        ${props.type === 'Ghost' ? `background-color: ${GHOST_HOVER_BACKGROUND_COLOR};` : ''}
        ${props.type === 'Primary' ? `background-color: var(--color-grey-2);` : ''}
        ${props.type === 'Danger' ? `background-color: ${DANGER_HOVER_BACKGROUND_COLOR};` : ''}
      `}
  }
`;

const getTextColor = ({
  type,
  hasLabel,
  isDisabled,
}: {
  type: ButtonType;
  hasLabel?: boolean;
  isDisabled?: boolean;
}) => {
  switch (type) {
    case 'Primary':
      return PRIMARY_TEXT_COLOR;
    case 'Secondary':
      return SECONDARY_TEXT_COLOR;
    case 'Selected':
      return SELECTED_TEXT_COLOR;
    case 'Tertiary':
      return TERTIARY_TEXT_COLOR;
    case 'Ghost':
      if (isDisabled) {
        return SECONDARY_TEXT_COLOR;
      } else if (hasLabel) {
        return GHOST_TEXT_COLOR;
      } else {
        return SECONDARY_TEXT_COLOR;
      }
    case 'Danger':
      return DANGER_TEXT_COLOR;
    case 'Danger Secondary':
      return DANGER_SECONDARY_TEXT_COLOR;
  }
};

/**
 * Primary UI component for user interaction
 */
const Button = ({
  size,
  icon,
  iconAfter,
  label,
  trailingLabel,
  isTrailingLabelLoading,
  type,
  onClick,
  isLoading,
  isDisabled,
  isDisabledMessage,
  hasNoPadding,
  testId,
  iconColor,
}: ButtonProps) => {
  const hasLabel = !!label && label.length > 0;
  const textColor = getTextColor({
    type,
    hasLabel,
    isDisabled,
  });
  const iconSize = size === 'X-Small' ? 14 : 16;

  return (
    <Tooltip
      content={isDisabled && isDisabledMessage ? isDisabledMessage : undefined}
    >
      <ButtonDiv
        size={size}
        type={type}
        onClick={isLoading || isDisabled ? undefined : onClick}
        hasLabel={hasLabel}
        isLoading={isLoading}
        isDisabled={isDisabled}
        hasNoPadding={hasNoPadding}
        data-testid={testId}
      >
        {icon && (
          <div style={{ marginRight: label ? 4 : 0, display: 'flex' }}>
            <Icon
              color={iconColor ? iconColor : textColor}
              icon={icon}
              height={iconSize}
              width={iconSize}
            />
          </div>
        )}
        <Typography.Body type={'Button Text'} color={textColor}>
          {label}
        </Typography.Body>
        {isTrailingLabelLoading ? (
          <div
            style={{ marginLeft: 2, display: 'flex' }}
            data-testid={testId ? `${testId}-trailing-loading` : undefined}
          >
            <Loading isTiny />
          </div>
        ) : (
          trailingLabel && (
            <div
              style={{
                marginLeft: 6,
                display: 'flex',
                fontStyle: 'italic',
                // The smaller text otherwise sits half a pixel above the
                // label's centre line
                position: 'relative',
                top: 0.5,
              }}
              data-testid={testId ? `${testId}-trailing-label` : undefined}
            >
              <Typography.Body
                type={'Body 12'}
                color={getTrailingTextColor(type)}
              >
                {trailingLabel}
              </Typography.Body>
            </div>
          )
        )}
        {isLoading && (
          <div style={{ marginLeft: 4 }}>
            <Loading isTiny />
          </div>
        )}
        {iconAfter && (
          <div style={{ marginLeft: 4, display: 'flex' }}>
            <Icon
              color={iconColor ? iconColor : textColor}
              icon={iconAfter}
              width={iconSize}
              height={iconSize}
            />
          </div>
        )}
      </ButtonDiv>
    </Tooltip>
  );
};

export default Button;
